import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { isUndefined } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DatatableColumn, DatatableSorting, FetchData, RktTableComponent } from '@rocketfinancialcorp/rocket-ui/table';

import { FinancialAccountDetails, FundOnHold, FundsOnHoldList, RequestPageParams } from '@shared/models';
import { FinancialAccountService } from '@shared/services';
import { ErrorUtils } from '@shared/utils';

@Component({
  selector: 'app-funds-on-hold-list',
  templateUrl: 'funds-on-hold-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RktTableComponent],
})
export class FundsOnHoldListComponent implements AfterViewChecked, OnDestroy {
  public financialAccountId?: FinancialAccountDetails['id'];

  public fundsOnHold: FundOnHold[] = [];

  public columns: DatatableColumn[] = [
    { name: 'Created At', prop: 'createdAt', fixedSize: 150, colType: 'date-time', isSortable: true, sortProp: 'createdAt' },
    { name: 'Fund On Hold ID', prop: 'id', fixedSize: 150, colType: 'short-id' },
    { name: 'Memo', prop: 'memo', flexSize: 300 },
    { name: 'Expiry Date', prop: 'expiry', fixedSize: 150, colType: 'date-time' },
    { name: 'Total Amount', prop: 'amount', flexSize: 150, colType: 'amount', colAlign: 'right' },
    { name: 'Status', prop: 'status', fixedSize: 140, colType: 'status-tag', colAlign: 'right' },
  ];

  sortParams: DatatableSorting = { key: 'createdAt', sortProp: 'createdAt', sortDir: 'desc' };

  public totalElements = 0;

  isLoading = false;

  emptyListMessage = 'No Funds on Hold found.';

  get activePage(): number {
    const routePage = this.activatedRoute?.snapshot?.queryParamMap?.get('page');
    return routePage ? parseInt(routePage, 10) : 0;
  }

  private destroy$ = new Subject<void>();

  constructor(
    public ref: ChangeDetectorRef,
    private financialAccountService: FinancialAccountService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
    this.financialAccountId = this.activatedRoute.snapshot?.parent?.paramMap.get('id') || undefined;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  getFundsOnHold({ page, size, sortParams }: FetchData): void {
    this.isLoading = true;
    this.updateQueryParams(page);

    if (sortParams) {
      this.sortParams = sortParams;
    }

    if (!this.financialAccountId) {
      return;
    }

    this.financialAccountService
      .getFundsOnHold({
        page,
        size: size || 10,
        financialAccountId: this.financialAccountId,
        sortParams: this.sortParams,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.emptyListMessage = 'No Funds on Hold found.';
          this.onFetchComplete(response);
        },
        error: (error) => {
          this.emptyListMessage = 'Unable to fetch Funds on Hold.';
          this.onFetchComplete();
          ErrorUtils.catchError('financialAccountService.getFundsOnHold error', error);
        },
      });
  }

  onFetchComplete(fundsOnHoldList?: FundsOnHoldList): void {
    const { content = [], totalElements = 0 } = fundsOnHoldList || {};
    this.fundsOnHold = content;
    this.totalElements = totalElements;
    this.isLoading = false;
  }

  updateQueryParams(page?: RequestPageParams['page']): void {
    if (!isUndefined(page) && this.activePage !== page) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { page: page > 0 ? page : undefined },
        queryParamsHandling: 'merge',
      });
    }
  }

  onRowClick(fundOnHold: FundOnHold): void {
    if (!fundOnHold.id || !this.financialAccountId) {
      return;
    }

    // Navigate to standalone fund hold details page
    this.router.navigate(['../../', this.financialAccountId, 'funds-on-hold', fundOnHold.id], {
      relativeTo: this.activatedRoute,
    });
  }
}
